import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { getAvailableSlots, createAppointment, getAppointmentTypes } from '../lib/api';
import { CheckCircle, Clock, CalendarIcon, User, Mail, Phone, FileText } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { calendarDayKeyInClinic, todayYmdInClinic } from '../lib/clinicTime';

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [typeSchedule, setTypeSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsAvailable, setSlotsAvailable] = useState(true);
  const [slotsReason, setSlotsReason] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    patient_name: '',
    patient_age: '',
    patient_email: '',
    patient_phone: '',
    reason: '',
    notes: ''
  });

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const data = await getAppointmentTypes();
        const list = Array.isArray(data) ? data : [];
        setAppointmentTypes(list);
        if (list.length > 0) {
          setSelectedTypeId(list[0].id);
          setTypeSchedule(list[0].schedule || null);
        }
      } catch (error) {
        console.error('Error loading appointment types:', error);
        toast.error('No se pudieron cargar los tipos de servicio.');
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    const t = appointmentTypes.find((x) => x.id === selectedTypeId);
    setTypeSchedule(t?.schedule || null);
    setSelectedDate(null);
    setSelectedTime(null);
    setStep(1);
  }, [selectedTypeId, appointmentTypes]);

  useEffect(() => {
    const loadSlots = async () => {
      if (!selectedDate || !selectedTypeId) return;
      setLoadingSlots(true);
      try {
        const dateStr = calendarDayKeyInClinic(selectedDate);
        const data = await getAvailableSlots(dateStr, { typeId: selectedTypeId });
        setAvailableSlots(data.slots || []);
        setSlotsAvailable(data.available);
        setSlotsReason(data.reason || '');
      } catch (error) {
        console.error('Error loading slots:', error);
        setAvailableSlots([]);
        setSlotsAvailable(false);
        setSlotsReason(
          error.response?.data?.detail ||
            'Sin conexión con el servidor. Si acabas de desplegar, asegúrate de usar la imagen nueva del frontend (proxy /api en nginx).'
        );
        toast.error('No se pudieron cargar los horarios.');
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
  }, [selectedDate, selectedTypeId]);

  const isDateDisabled = (date) => {
    const ymd = calendarDayKeyInClinic(date);
    if (ymd && ymd < todayYmdInClinic()) return true;
    if (!typeSchedule) return false;
    const dayOfWeek = date.getDay();
    const isoDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    return !typeSchedule.working_days.includes(isoDay);
  };

  const handleDateSelect = (date) => {
    // react-day-picker puede llamar onSelect(undefined) al re-pulsar el día; no borrar la selección.
    if (!date) return;
    setSelectedDate(date);
    setSelectedTime(null);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTypeId || !selectedDate || !selectedTime) {
      toast.error('Por favor selecciona tipo de servicio, fecha y hora');
      return;
    }
    
    setSubmitting(true);
    try {
      const emailTrim = formData.patient_email.trim();
      const ageRaw = String(formData.patient_age ?? '').trim();
      const age = ageRaw ? parseInt(ageRaw, 10) : null;
      if (ageRaw && (!Number.isFinite(age) || age < 1 || age > 120)) {
        toast.error('Si indicas edad, debe ser entre 1 y 120 años');
        setSubmitting(false);
        return;
      }
      const appointmentData = {
        ...formData,
        ...(age != null ? { patient_age: age } : {}),
        ...(emailTrim ? { patient_email: emailTrim } : {}),
        date: calendarDayKeyInClinic(selectedDate),
        time: selectedTime,
        appointment_type_id: selectedTypeId,
      };
      if (!emailTrim) {
        delete appointmentData.patient_email;
      }
      if (!ageRaw) {
        delete appointmentData.patient_age;
      }
      await createAppointment(appointmentData);
      setSuccess(true);
      toast.success('Solicitud registrada. Espera la confirmación del administrador.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al agendar la cita';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background py-12" data-testid="booking-success">
        <div className="max-w-lg mx-auto px-4">
          <Card className="border border-border p-8 text-center">
            <CardContent className="p-0 space-y-6">
              <div className="w-16 h-16 mx-auto bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-success" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground">
                ¡Cita Agendada!
              </h1>
              <div className="bg-sage-50 rounded-xl p-4 space-y-2 text-left">
                <p><strong>Fecha:</strong> {format(selectedDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}</p>
                <p><strong>Hora:</strong> {selectedTime}</p>
                <p><strong>Paciente:</strong> {formData.patient_name}</p>
              </div>
              <p className="text-muted-foreground text-sm">
                El administrador revisará tu solicitud. Si indicaste teléfono, te enviaremos un WhatsApp cuando la cita quede confirmada.
              </p>
              <Button onClick={() => navigate('/')} className="rounded-full" data-testid="back-home-btn">
                Volver al Inicio
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="booking-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
            Agendar Cita
          </h1>
          <p className="text-muted-foreground text-lg">
            Selecciona la fecha y hora que mejor te convenga
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-8" data-testid="booking-steps">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s ? 'bg-primary text-primary-foreground' : 'bg-border text-muted-foreground'
                }`}
              >
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-border'}`} />}
            </div>
          ))}
        </div>

        <Card className="border border-border mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Tipo de servicio</CardTitle>
          </CardHeader>
          <CardContent>
            {appointmentTypes.length === 0 ? (
              <p className="text-muted-foreground text-sm">Cargando servicios…</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {appointmentTypes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTypeId(t.id)}
                    className={`p-4 rounded-xl border text-left transition-colors ${
                      selectedTypeId === t.id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <p className="font-medium">{t.label}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t.room_count} consultorio{t.room_count !== 1 ? 's' : ''} disponible
                      {t.room_count !== 1 ? 's' : ''} por hora
                    </p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <Card className={`border ${step === 1 ? 'border-primary' : 'border-border'}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarIcon className="w-5 h-5 text-primary" />
                1. Selecciona Fecha
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                required
                selected={selectedDate}
                onSelect={handleDateSelect}
                disabled={isDateDisabled}
                fromDate={new Date()}
                toDate={addDays(new Date(), 60)}
                locale={es}
                className="rounded-xl border"
                data-testid="booking-calendar"
              />
            </CardContent>
          </Card>

          {/* Time Slots */}
          <Card className={`border ${step === 2 ? 'border-primary' : 'border-border'}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5 text-primary" />
                2. Selecciona Hora
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedDate ? (
                <p className="text-muted-foreground text-center py-8">
                  Primero selecciona una fecha
                </p>
              ) : loadingSlots ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-muted-foreground">Cargando horarios...</div>
                </div>
              ) : !slotsAvailable ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No hay horarios disponibles</p>
                  <p className="text-sm mt-2">{slotsReason}</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No hay horarios disponibles para esta fecha
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto" data-testid="time-slots">
                  {availableSlots.map((time) => (
                    <button
                      key={time}
                      onClick={() => handleTimeSelect(time)}
                      className={`p-3 rounded-lg text-sm font-medium transition-colors ${
                        selectedTime === time
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-sage-50 text-foreground hover:bg-sage-100'
                      }`}
                      data-testid={`time-slot-${time}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Form */}
          <Card className={`border ${step === 3 ? 'border-primary' : 'border-border'}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-primary" />
                3. Tus Datos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedTime ? (
                <p className="text-muted-foreground text-center py-8">
                  Primero selecciona fecha y hora
                </p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                  <div>
                    <Label htmlFor="patient_name" className="flex items-center gap-1 mb-1">
                      <User className="w-3 h-3" /> Nombre completo *
                    </Label>
                    <Input
                      id="patient_name"
                      name="patient_name"
                      value={formData.patient_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Tu nombre"
                      className="h-11"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="patient_age" className="flex items-center gap-1 mb-1">
                      Edad (años, opcional)
                    </Label>
                    <Input
                      id="patient_age"
                      name="patient_age"
                      type="number"
                      min={1}
                      max={120}
                      value={formData.patient_age}
                      onChange={handleInputChange}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="patient_email" className="flex items-center gap-1 mb-1">
                      <Mail className="w-3 h-3" /> Email (opcional)
                    </Label>
                    <Input
                      id="patient_email"
                      name="patient_email"
                      type="email"
                      value={formData.patient_email}
                      onChange={handleInputChange}
                      placeholder="tu@email.com"
                      className="h-11"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="patient_phone" className="flex items-center gap-1 mb-1">
                      <Phone className="w-3 h-3" /> Teléfono *
                    </Label>
                    <Input
                      id="patient_phone"
                      name="patient_phone"
                      value={formData.patient_phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+52 1 000 000 0000"
                      className="h-11"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reason" className="flex items-center gap-1 mb-1">
                      <FileText className="w-3 h-3" /> Motivo de la cita
                    </Label>
                    <Textarea
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      placeholder="Describe brevemente el motivo..."
                      rows={3}
                      data-testid="input-reason"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full"
                    disabled={submitting}
                    data-testid="submit-booking-btn"
                  >
                    {submitting ? 'Agendando...' : 'Confirmar Cita'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Selected Summary */}
        {(selectedDate || selectedTime) && (
          <Card className="mt-6 border border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                {selectedDate && (
                  <span className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-primary" />
                    {format(selectedDate, "EEEE, d 'de' MMMM", { locale: es })}
                  </span>
                )}
                {selectedTime && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    {selectedTime}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
